import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Phase 2A — read-only applicability context for an assessment.
 *
 * The engine is installed but DISABLED by default
 * (public.applicability_settings.applicability_enabled = false). While it is
 * off, `assessment_applicable_items` returns the complete questionnaire, so
 * every consumer of this hook sees today's behaviour unchanged.
 *
 * No UI filtering is driven from the client. Filtering is a server concern —
 * this hook exists purely so surfaces can display the frozen question set,
 * stable ordinals and lock state.
 */

export interface ApplicabilityItem {
  question_id: number;
  display_ordinal: number;
  display_order: number;
  domain: string | null;
  requires_evidence: boolean | null;
  evidence_threshold: number | null;
}

export function useApplicabilityEnabled() {
  return useQuery({
    queryKey: ["applicability-enabled"],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<boolean> => {
      const { data, error } = await (supabase.rpc as any)("applicability_enabled");
      if (error) throw error;
      return Boolean(data);
    },
  });
}

export function useAssessmentApplicability(assessmentId?: string | null) {
  return useQuery({
    queryKey: ["assessment-applicability", assessmentId],
    enabled: Boolean(assessmentId),
    staleTime: 60 * 1000,
    queryFn: async (): Promise<ApplicabilityItem[]> => {
      const { data, error } = await (supabase.rpc as any)("assessment_applicable_items", {
        p_assessment_id: assessmentId,
      });
      if (error) throw error;
      return (data ?? []) as ApplicabilityItem[];
    },
  });
}

/** Stable question number for reports/UI — never derive numbering from array index. */
export function ordinalFor(
  items: ApplicabilityItem[] | undefined,
  questionId: number,
): number | null {
  return items?.find((i) => Number(i.question_id) === Number(questionId))?.display_ordinal ?? null;
}
